import { useEffect, useState, useCallback } from 'react';
import { statesTranslate } from '../../models/OrderEntity';

// Service
import OrderService from '../../services/orderService';
import salesService from '../../services/salesService';

// Hooks
import useUser from '../user/useUser';
import useNotify from '../notify';
import { hasIndeterminatePosnetPayment } from '../../utils/payment-utils';

const mapOrder = (order) => {
    if (!order) return null;

    return {
        ...order,
        stateToShow: statesTranslate[order.state],
        items: order.items.map(item => ({
            ...item,
            refundedQuantity: order.operations
                .filter(o => o.state === 'Approved')
                .map(opt => opt.items)
                .reduce((a, b) => a.concat(b), [])
                .filter(i => i.refId === item.refId && i.warehouseId === item.warehouseId)
                .reduce((quantity, item) => quantity + item.quantity, 0)
        }))
    }
}

const useOrder = ({ orderId, selectedOrder = null }) => {
    const [order, setOrder] = useState({});
    const [loading, setLoading] = useState(false);
    const { branch, pv } = useUser();
    const { Notify, variants } = useNotify();

    useEffect(() => {
        const getFromServer = () => {
            setLoading(true);
            OrderService
                .Get(orderId)
                .then(res => setOrder(mapOrder(res)))
                .finally(() => setLoading(false));
        }

        if (orderId && !selectedOrder)
            getFromServer();

        if (selectedOrder)
            setOrder(mapOrder(selectedOrder))
    }, [orderId, selectedOrder])

    const invoice = useCallback(async (manualInvoiceData, refundItems) => {
        setLoading(true);
        const order = await OrderService
            .Invoice(orderId, manualInvoiceData, refundItems)
            .finally(() => setLoading(false));

        setOrder(mapOrder(order));
        return order;
    }, [orderId])

    const voidOrder = useCallback(async (extraData) => {
        setLoading(true);
        const order = await OrderService
            .VoidOrder(orderId, extraData, branch.warehouseId, pv.billingCode)
            .finally(() => setLoading(false));

        setOrder(mapOrder(order));
        return order;
    }, [orderId, branch.warehouseId, pv.billingCode])

    const refund = useCallback(async (model) => {
        setLoading(true);
        const order = await OrderService
            .Refund(orderId, model, branch.warehouseId, pv.billingCode)
            .finally(() => setLoading(false));

        setOrder(mapOrder(order));
        return order;
    }, [orderId, branch.warehouseId, pv.billingCode])

    const retryOperation = useCallback(async () => {
        setLoading(true);
        const order = await OrderService
            .RetryOperation(orderId)
            .finally(() => setLoading(false));

        setOrder(mapOrder(order));
        return order;
    }, [orderId, branch.warehouseId])

    const paymentsCancelation = useCallback(async () => {
        setLoading(true);
        const order = await OrderService
            .PaymentsCancelation(orderId)
            .finally(() => setLoading(false));

        setOrder(mapOrder(order));
        return order;
    }, [orderId])

    const updateCustomer = (customer) =>
        setOrder(order => mapOrder({ ...order, customer }))

    const cancelSale = useCallback(async () => {
        setLoading(true);
        const order = await salesService
            .SaleCancelation(orderId)
            .finally(() => setLoading(false));

        setOrder(mapOrder(order));
        return order;
    }, [orderId, branch.warehouseId])

    const cancelOperation = useCallback(async () => {
        setLoading(true);
        try {
            const order = await OrderService.CancelOperation(orderId)
            setOrder(mapOrder(order));
            return order;
        } catch (err) {
            console.error(err);
            Notify(err?.message ?? "Ocurrio un error al cancelar la operación.", variants.ERROR);
        } finally {
            setLoading(false)
        }
    }, [orderId, branch.warehouseId])

    const getCurrentOrder = async () => {
        if (pv.hasTerminal || !hasIndeterminatePosnetPayment(order))
            return order

        setLoading(true)
        const currentOrder = await OrderService.Get(order.id)
        setLoading(false)

        return currentOrder
    }

    return {
        order,
        loading,
        invoice,
        voidOrder,
        refund,
        retryOperation,
        paymentsCancelation,
        updateCustomer,
        cancelSale,
        cancelOperation,
        getCurrentOrder
    };
}

export default useOrder;
